import type { Store as StoreService } from '@warp-drive/core';
import { exportReport } from 'tio-data/requests/search';
import { sqlExportReport, type SqlReportExportResponse } from 'tio-data/requests/sql-dataset';
import type { ReportExportResponse } from 'tio-data/data/resources/search.ext';
import { buildReportSearchBody } from '../report-search-body.ts';
import {
  getCompanyFilterField,
  getModelTypeFromDataset,
  getSearchRequestModelType,
  isSqlDataset,
} from '../dataset-utils.ts';
import type { ColumnFormatConfig } from '../column-format-options.ts';
import type { FieldOption } from '../opensearch-field-mapper.ts';
import { effectiveWidgetFilters, type WidgetFilterInputs } from './effective-widget-filters.ts';
import { widgetExportBlocker, widgetExportDefinition } from './widget-export-definition.ts';
import { resolveDatasetFields } from './widget-resolver.ts';
import { SQL_SCOPE_REQUIRED_MESSAGE, sqlScopeUnavailable } from './widget-data-adapter.ts';
import type { WidgetConfig } from './widget-config-types.ts';

export interface WidgetExportRequestInput extends WidgetFilterInputs {
  widget: WidgetConfig;
  /** The dataset's full registry (resolveDatasetFields), not just the widget's columns. */
  fields: FieldOption[];
  /** The company every dashboard query runs against; an unscoped dashboard cannot export. */
  companyId?: string;
}

export interface WidgetExportRequest {
  /** The /search request model — TasApplication when the widget shows individual applications. */
  modelType: string;
  /** Routes to `sqlExportReport` instead of `exportReport`. */
  isSql: boolean;
  companyId?: string;
  searchBody: Record<string, unknown>;
  columnKeys: string[];
  columnLabels?: Record<string, string>;
  columnFormats?: Record<string, ColumnFormatConfig>;
}

/**
 * Assemble the server export request for one widget from exactly what it
 * renders with: the shared filter merge plus its tabular definition, through
 * the same body builder the report viewer's export uses.
 *
 * @param input the widget, its dataset fields, the dashboard scope and the live filters/drilldowns
 * @returns the body embeds the company term for OpenSearch and leaves it to the request param
 *   for SQL (the viewer's posture for each endpoint); `columnKeys` keep the document-count column
 *   even though no dataset field backs it, so it never reaches `_source` or the metric aggs
 */
export function buildWidgetExportRequest(input: WidgetExportRequestInput): WidgetExportRequest {
  const { widget, fields, companyId } = input;
  const modelType = getSearchRequestModelType(
    getModelTypeFromDataset(widget.dataset),
    widget.showIndividualApplicationsOnly ?? true
  );
  const definition = widgetExportDefinition(widget);
  const filters = effectiveWidgetFilters(widget, input);
  const byKey = new Map(fields.map((field) => [field.key, field]));
  const columns = definition.selectedColumns
    .map((key) => byKey.get(key))
    .filter((field): field is FieldOption => Boolean(field));

  const searchBody = buildReportSearchBody({
    modelType,
    fields,
    buildFilterValues: filters.buildFilterValues,
    buildFilterLayout: filters.buildFilterLayout,
    userFilterValues: filters.userFilterValues,
    extraFilterValues: filters.extraFilterValues,
    companyId,
    companyFilterField: getCompanyFilterField(widget.dataset),
    forceSqlCompanyTerm: false,
    isAggregated: definition.isAggregated,
    metricFiltersAsHaving: false,
    groupByFieldKeys: definition.groupBy?.selectedFields,
    columns,
    sorts: definition.sorts,
    columnAggregationMap: definition.groupBy?.columnAggregations,
    dateGroupingIntervals: definition.groupBy?.dateGroupingIntervals,
  });

  return {
    modelType,
    isSql: isSqlDataset(modelType),
    companyId,
    searchBody,
    columnKeys: definition.selectedColumns,
    columnLabels: definition.columnLabels,
    columnFormats: definition.columnFormats,
  };
}

/** The widget cannot be exported as configured; the message is written for the viewer. */
export class WidgetExportUnavailableError extends Error {}

export interface ExportWidgetCsvOptions extends WidgetFilterInputs {
  store: StoreService;
  /** The per-app `ENV.apiKey` — tio-data is owner-free, so the caller supplies it. */
  apiKey: string;
  /** The dashboard report the widget belongs to; the export URL is keyed on it. */
  dashboardId: string;
  widget: WidgetConfig;
  companyId?: string;
}

/**
 * Download one widget's data as CSV through the server export path (gate,
 * audit row, S3 asset and formatting pipeline shared with report exports).
 * Fetches the dataset's field registry, then POSTs to the dashboard's export
 * endpoint and triggers the browser download of the signed file.
 *
 * @returns the export envelope; a SQL result may carry `truncated` when the row cap cut it short
 * @throws WidgetExportUnavailableError when the widget has nothing to export, its layout
 *   would lose a measure, or its SQL dataset needs a company the dashboard lacks
 * @throws Error when the server rejects the export or the signed-URL download fails
 */
export async function exportWidgetCsv(
  options: ExportWidgetCsvOptions
): Promise<ReportExportResponse | SqlReportExportResponse> {
  const { store, apiKey, dashboardId, widget, companyId } = options;
  const individual = widget.showIndividualApplicationsOnly ?? true;
  const modelType = getSearchRequestModelType(getModelTypeFromDataset(widget.dataset), individual);
  if (sqlScopeUnavailable(modelType, companyId)) {
    throw new WidgetExportUnavailableError(SQL_SCOPE_REQUIRED_MESSAGE);
  }
  if (!widgetExportDefinition(widget).selectedColumns.length) {
    throw new WidgetExportUnavailableError('This widget has no data to download.');
  }
  const blocker = widgetExportBlocker(widget);
  if (blocker) throw new WidgetExportUnavailableError(blocker);

  const fields = await resolveDatasetFields(store, widget.dataset, companyId, individual);
  const request = buildWidgetExportRequest({
    widget,
    fields,
    companyId,
    buildFilters: options.buildFilters,
    userFilters: options.userFilters,
    drilldowns: options.drilldowns,
  });
  const shared = {
    apiKey,
    companyId,
    widgetId: widget.id,
    searchBody: request.searchBody,
    columnKeys: request.columnKeys,
    columnLabels: request.columnLabels,
    columnFormats: request.columnFormats,
  };
  if (request.isSql) {
    return sqlExportReport(dashboardId, { ...shared, dataset: request.modelType });
  }
  return exportReport(dashboardId, { ...shared, modelType: request.modelType });
}
